import type { ComponentProps } from 'react';

import { Link, useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { NavMain } from '@/components/layout/NavMain';
import { NavUser } from '@/components/layout/NavUser';
import { Typography } from '@/components/Typography';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarRail,
} from '@/components/ui/sidebar';
import { VeraLogo } from '@/components/VeraLogo';
import { menuConfig } from '@/config/menu.config';
import { useAuthContext } from '@/data/hooks/auth';
import { getVisibleMenuItems } from '@/utils/menu.utils';

export const AppSidebar = (props: ComponentProps<typeof Sidebar>) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { isAuthenticated } = useAuthContext();
  const items = getVisibleMenuItems(menuConfig.items, isAuthenticated);

  return (
    <Sidebar collapsible='icon' expandOnHover {...props}>
      <SidebarHeader className='px-3'>
        <SidebarMenu>
          <SidebarMenuItem>
            <Link to='/' className='flex h-12 items-center gap-2' data-testid='sidebar-brand'>
              <div className='flex aspect-square size-9 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground'>
                <VeraLogo />
              </div>
              <div className='grid min-w-0 leading-tight transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:opacity-0'>
                <Typography variant='paragraphSmallBold' color='foreground' className='truncate'>
                  Vera
                </Typography>
                <Typography variant='paragraphMini' color='muted' className='truncate'>
                  {t('homepage.subtitle')}
                </Typography>
              </div>
            </Link>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      {/* Pendant l'animation de largeur, les libellés (nowrap) débordent : pas de scroll horizontal. */}
      <SidebarContent className='overflow-x-hidden pt-2'>
        <NavMain items={items} pathname={pathname} />
      </SidebarContent>
      <SidebarFooter className='border-t p-0'>
        <NavUser />
      </SidebarFooter>
      <SidebarRail className='outline-none hover:after:bg-transparent group-data-[side=left]:right-0 ltr:translate-x-0' />
    </Sidebar>
  );
};
